/**
 * /reports — costo por unidad + export CSV
 */
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { KpiCard } from '@/components/ui/kpi-card'
import { Skeleton } from '@/components/ui/skeleton'
import { getReports } from '@/lib/api'

function downloadCsv(filename: string, rows: Record<string, string | number | null>[]) {
  if (rows.length === 0) return
  const headers = Object.keys(rows[0]!)
  const escape = (v: string | number | null) => {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const body = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h] ?? null)).join(',')),
  ].join('\n')
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function ReportsPage() {
  const query = useQuery({ queryKey: ['reports'], queryFn: getReports })

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <Card>
        <EmptyState
          title="No se pudieron cargar los reportes"
          description={(query.error as Error | null)?.message}
        />
      </Card>
    )
  }

  const { kpis, units } = query.data

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Costo de por vida por unidad"
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              downloadCsv(
                'gkdfleet-cost-by-unit.csv',
                units.map((u) => ({
                  unit: u.unitNumber,
                  type: u.vehicleType,
                  odometer: u.currentOdometer,
                  repairs: u.repairs,
                  other: u.other,
                  total: u.total,
                  cost_per_distance: u.costPerUnitDistance,
                })),
              )
            }
          >
            <Download className="size-4" aria-hidden />
            Export CSV
          </Button>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Units" value={kpis.units} />
        <KpiCard label="Repairs" value={`$${kpis.repairs.toLocaleString()}`} />
        <KpiCard label="Other" value={`$${kpis.other.toLocaleString()}`} />
        <KpiCard label="Total" value={`$${kpis.total.toLocaleString()}`} />
      </div>

      <Card className="overflow-x-auto">
        <CardBody className="p-0">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-border bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Unit</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Odometer</th>
                <th className="px-4 py-3 font-medium">Repairs</th>
                <th className="px-4 py-3 font-medium">Other</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">$/dist</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {units.map((u) => (
                <tr key={u.vehicleId}>
                  <td className="px-4 py-3 font-medium">{u.unitNumber}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.vehicleType}</td>
                  <td className="px-4 py-3">
                    {u.currentOdometer.toLocaleString()} {u.odometerUnit}
                  </td>
                  <td className="px-4 py-3">${u.repairs.toLocaleString()}</td>
                  <td className="px-4 py-3">${u.other.toLocaleString()}</td>
                  <td className="px-4 py-3 font-medium">${u.total.toLocaleString()}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {u.costPerUnitDistance != null ? u.costPerUnitDistance.toFixed(4) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </>
  )
}
